import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { Button, Card, Flex } from '@radix-ui/themes'
import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import {
	type ShouldRevalidateFunctionArgs,
	useFetcher,
	useLoaderData,
	useLocation,
	useNavigation,
	useOutletContext,
} from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RxPlus } from 'react-icons/rx'
import { getFetcherType } from 'remix-utils/fetcher-type'
import { z } from 'zod'

import { FormDefaults } from '~/components/common/form-defaults.tsx'
import { SelectCountryCode } from '~/components/common/select-country-code'
import { SubmissionButton } from '~/components/common/submission-button.tsx'
import { Checkbox } from '~/components/ui/checkbox'
import { TextField } from '~/components/ui/text-field.tsx'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import { Component as ProfileAddressesIndexComponent } from '~/routes/($lang).profile.addresses._index/index.component'
import { type loader } from '~/routes/($lang).profile.addresses.create/create.server'
import { useProfileAddressesCreateNotify } from '~/routes/($lang).profile.addresses.create/use-profile-addresses-create-notify.tsx'
import {
	type Context,
	type FormEnum,
	ROOT_INPUTS,
} from '~/routes/_layout/root.component'
import { metaDefault } from '~/utils/client/meta-default.ts'
import { schemaDefault } from '~/utils/form-defaults.ts'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeProfileAddressesCreate = 'route-profile-addresses-create',
	uiSelectCountryCode = 'ui-select-country-code',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang).profile.addresses.create',
	profileAddresses = 'routes/($lang).profile.addresses',
}

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SEARCH_PARAMS {
	firstName = 'firstName',
	lastName = 'lastName',
	phoneNumber = 'phoneNumber',
	company = 'company',
	street = 'street',
	city = 'city',
	postcode = 'postcode',
	countryCode = 'countryCode',
	defaultAddress = 'defaultAddress',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	firstName = 'firstName',
	lastName = 'lastName',
	phoneNumber = 'phoneNumber',
	company = 'company',
	street = 'street',
	city = 'city',
	postcode = 'postcode',
	countryCode = 'countryCode',
	defaultAddress = 'defaultAddress',
}

/* -------------------------------------------------------------------------------------------------
 * Form
 * -----------------------------------------------------------------------------------------------*/

const FORM: FormEnum = {
	id: 'create-address-form',
	action: '/profile/addresses/create',
	fetcherKey: 'create-address',
	subaction: 'create-address',
	redirect: '/profile/addresses',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.firstName]: z
			.string({ required_error: 'validation.firstName.required' })
			.min(2, 'validation.firstName.min')
			.max(255, 'validation.firstName.max'),
		[INPUTS.lastName]: z
			.string({ required_error: 'validation.lastName.required' })
			.min(2, 'validation.lastName.min')
			.max(255, 'validation.lastName.max'),
		[INPUTS.phoneNumber]: z
			.string()
			.min(2, 'validation.phoneNumber.min')
			.max(255, 'validation.phoneNumber.max')
			.optional(),
		[INPUTS.company]: z
			.string()
			.min(2, 'validation.company.min')
			.max(255, 'validation.company.max')
			.optional(),
		[INPUTS.street]: z
			.string({ required_error: 'validation.street.required' })
			.min(2, 'validation.street.min')
			.max(255, 'validation.street.max'),
		[INPUTS.city]: z
			.string({ required_error: 'validation.city.required' })
			.min(2, 'validation.city.min')
			.max(255, 'validation.city.max'),
		[INPUTS.postcode]: z
			.string({ required_error: 'validation.postcode.required' })
			.min(2, 'validation.postcode.min')
			.max(255, 'validation.postcode.max'),
		[INPUTS.countryCode]: z.string({
			required_error: 'validation.countryCode.required',
		}),
		[INPUTS.defaultAddress]: z.boolean().optional(),
	},
})

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<typeof loader> = (props: MetaArgs) =>
	metaDefault({ ...props, ROUTE_LOADER, I18NEXT })

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC = () => {
	const { addresses } = useLoaderData<typeof loader>()
	const { locale, setApp } = useOutletContext<Context>()
	const location = useLocation()
	const navigation = useNavigation()
	const fetcher = useFetcher({ key: FORM.subaction })
	const fetcherType = getFetcherType(fetcher, navigation)
	const { t } = useTranslation(I18NEXT.routeProfileAddressesCreate)
  const formValues = {
    [ROOT_INPUTS.subaction]: FORM.subaction,
    [ROOT_INPUTS.noJs]: 'true',
  }
	const [form, fields] = useForm({
		lastResult: fetcher.data || undefined,
		defaultValue: {
			...formValues,
			[INPUTS.countryCode]: 'DE',
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema })
		},
		shouldValidate: 'onBlur',
	})

	useProfileAddressesCreateNotify({ setApp, fetcher, t })

	return fetcherType != 'init' ? (
		<ProfileAddressesIndexComponent addresses={addresses} />
	) : (
		<Flex justify="center" width="100%" mt={{ initial: '5', lg: '0' }}>
			<Card size="5">
				<FormProvider context={form.context}>
					<fetcher.Form
						method="post"
						action={`/${locale}${FORM.action}${location.search}`}
						{...getFormProps(form)}
					>
						<FormDefaults
              form={form}
              fields={fields}
              values={formValues}
            />
						<Flex direction="column" gapY="5" mt="2">
							{[
								{ title: INPUTS.firstName },
								{ title: INPUTS.lastName },
								{ title: INPUTS.phoneNumber, type: 'tel' },
								{ title: INPUTS.company },
								{ title: INPUTS.street },
								{ title: INPUTS.city },
								{ title: INPUTS.postcode, type: 'tel' },
							].map(({ title, type = 'text' }) => (
								<TextField key={title} type={type} meta={fields[title]} t={t}>
									{t(`label.${title}`)}
								</TextField>
							))}
							<SelectCountryCode
								meta={fields[INPUTS.countryCode]}
								t={t}
								placeholder="???????????"
							>
								???????????
							</SelectCountryCode>
							<Checkbox t={t} meta={fields[INPUTS.defaultAddress]}>
								{t('button.setAsDefaultAddress')}
							</Checkbox>
							<Button size="4" type="submit">
								<SubmissionButton fetcher={fetcher}>
									<RxPlus size="1.2em" />
									{t('button.createAddressSubmit')}
								</SubmissionButton>
							</Button>
						</Flex>
					</fetcher.Form>
				</FormProvider>
			</Card>
		</Flex>
	)
}
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
	Component,
	shouldRevalidate,
	handle,
	meta,
	I18NEXT as PROFILE_ADDRESSES_CREATE_I18NEXT,
	ROUTE_LOADER as PROFILE_ADDRESSES_CREATE_ROUTE_LOADER,
	SEARCH_PARAMS as PROFILE_ADDRESSES_CREATE_SEARCH_PARAMS,
	INPUTS as PROFILE_ADDRESSES_CREATE_INPUTS,
	FORM as PROFILE_ADDRESSES_CREATE_FORM,
	schema as profileAddressesCreateSchema,
}
